import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { CostOverrideType } from '../CostOverrideType';
import { GET_PROJECT_COST_RATE_TABLE_DIMENSIONS } from './useCostOverrideTableDimensions';

export const PUT_PROJECT_COST_RATES = gql`
  mutation putProjectCostRates(
    $projectId: String!
    $dimensionUris: [String!]
    $requiredDimensionUris: [String!]
    $rates: [ProjectRateInput3!]
  ) {
    putProjectCostRates(
      projectId: $projectId
      dimensionUris: $dimensionUris
      requiredDimensionUris: $requiredDimensionUris
      rates: $rates
    ) {
      id
      dimensions {
        dimensionUri
        objectUri
      }
      scheduleEntries {
        amount {
          amount
          currency {
            id
          }
        }
      }
    }
  }
`;

export const mapProjectRatesToCostRateInput = (
  selectedDimensions,
  projectRates
) => {
  const groupMap = projectRates.reduce((acc, rate) => {
    const { groupId } = rate;

    if (!acc[groupId]) {
      acc[groupId] = [];
    }
    acc[groupId].push(rate);

    return acc;
  }, {});

  return Object.values(groupMap).map(groupRates => {
    const firstRate = groupRates[0];
    const dimensions = selectedDimensions
      .filter(d => firstRate[d.id]?.value)
      .map(d => ({
        dimensionUri: d.id,
        objectUri: firstRate[d.id].value.id
      }));

    const entries = groupRates.map(rate => {
      const isPercentMarkup = rate.type === CostOverrideType.PERCENT_MARKUP;
      const hasAmount = rate.amount?.amount > 0;

      return {
        amount: {
          amount: !isPercentMarkup && hasAmount ? rate.amount.amount : 0,
          currency: { id: rate.amount?.currency?.id }
        },
        ...(isPercentMarkup ? { percentMarkup: rate.percent } : {}),
        effectiveDate: rate.effectiveDate
      };
    });

    return { dimensions, entries };
  });
};

const dimensionSignature = (rate, selectedDimensions) =>
  selectedDimensions.map(d => rate[d.id]?.value?.id ?? '').join('|');

export const mapRemovedCostRateGroupsInput = (
  selectedDimensions,
  persistedRates = [],
  projectRates
) => {
  const workingSignatures = new Set(
    projectRates.map(rate => dimensionSignature(rate, selectedDimensions))
  );

  const removedRates = persistedRates.filter(
    rate => !workingSignatures.has(dimensionSignature(rate, selectedDimensions))
  );

  return mapProjectRatesToCostRateInput(
    selectedDimensions,
    removedRates
  ).map(group => ({ ...group, entries: [] }));
};

export const updateCostRateDimensionsCache = ({
  projectId,
  updatedDimensions
}) => cache => {
  const stored = cache.readQuery({
    query: GET_PROJECT_COST_RATE_TABLE_DIMENSIONS,
    variables: { projectId }
  });

  if (!stored?.project?.costRateTableDimension) return;

  // selectedDimensions are display-layer objects enriched by
  // OVERRIDE_DIMENSION_PROPS (e.g. sortIndex). Strip UI-only fields so the
  // cached shape stays consistent with the GraphQL schema for this query.
  const dimensions = updatedDimensions.map(
    ({ sortIndex, ...dimension }) => dimension
  );

  cache.writeQuery({
    query: GET_PROJECT_COST_RATE_TABLE_DIMENSIONS,
    variables: { projectId },
    data: {
      project: {
        ...stored.project,
        costRateTableDimension: {
          ...stored.project.costRateTableDimension,
          dimensions
        }
      }
    }
  });
};

export const buildProjectCostRatesMutationOptions = ({
  projectId,
  dimensionUris,
  rates,
  updatedDimensions
}) => ({
  variables: {
    projectId,
    dimensionUris,
    requiredDimensionUris: [],
    rates
  },
  // Only write the dimensions cache when the dimension set actually changed (a
  // put). Rewriting it on an in-place update would recompute the mapped rates
  // against still-stale rate data mid-save and briefly flash the pre-edit value.
  ...(updatedDimensions
    ? {
        update: updateCostRateDimensionsCache({
          projectId,
          updatedDimensions
        })
      }
    : {})
});

export const usePutProjectCostRates = () => {
  const [putProjectCostRatesMutation, { loading }] = useMutation(
    PUT_PROJECT_COST_RATES
  );

  return {
    putProjectCostRates: async ({
      projectId,
      selectedDimensions,
      projectRates
    }) => {
      const rates = mapProjectRatesToCostRateInput(
        selectedDimensions,
        projectRates
      );
      const dimensionUris = selectedDimensions.map(d => d.id);

      return putProjectCostRatesMutation(
        buildProjectCostRatesMutationOptions({
          projectId,
          dimensionUris,
          rates,
          updatedDimensions: selectedDimensions
        })
      );
    },
    isSaving: loading
  };
};
